/**
 * Device CSV imports — the ingest half of the measurements feature.
 *
 * Five near-identical pacmny-be routes (`/file/uploadBloodPressure`,
 * `uploadWeightBloodPressure`, `uploadFore`, `uploadFreeStyleLibreLink`,
 * `uploadFreeStyleLibreLink2`) collapse onto one endpoint,
 * `POST /device-imports`, where `source` is what used to be the difference
 * between them. None of the five had a bridge entry — the bridge intercepts the
 * URL because it contains `index.php` and then found no route, no `#action` and
 * no documented gap — so uploading any device file threw
 * `BridgeUnmappedOperation`. The reading half (`/data/externalMeasures` and
 * friends) is mapped, so this was the only dead half of the feature.
 *
 * All five legacy actions read `$_FILES['file-field']` with no MIME, size or
 * extension check (`pacmny-be/controllers/FileUploadRequestHandler.php:9`); the
 * replacement validates all three, and the chart is named explicitly rather than
 * inferred from a session.
 *
 * The call sites still hand in a `FormData` carrying `file-field`, because
 * `DataUploadPage` and `FileUpload` build it that way and neither is being
 * rewritten in this pass — the translation to the new part names lives here.
 */

import { createDeviceImport, getDeviceImport } from '@/api2/devices';
import { usePatientContextStore } from '@/stores/patientContext';

/**
 * How long to keep asking whether the import finished.
 *
 * The upload answers `202` and the rows are mapped on a queue, but every caller
 * treats a resolved promise as "the data is on the chart" and immediately tells
 * the user to go and look at it. Polling here keeps that promise honest without
 * touching the two upload screens. The ceiling exists so a stuck queue surfaces
 * as a message rather than a spinner that never stops.
 */
const IMPORT_POLL_INTERVAL_MS = 1000;
const IMPORT_POLL_ATTEMPTS = 30;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The chart the readings belong to.
 *
 * Named in the request, and checked by the API against the caller's care
 * relationship. It is *not* tenancy — the organization still comes from the
 * access token — which is the distinction the legacy uploads collapsed by taking
 * the patient from the body and checking nothing.
 */
function currentPatientId() {
  const patientId = usePatientContextStore().patientId;

  if (!patientId) {
    // Loud, because the alternative is filing a patient's readings onto
    // whichever chart the server guessed. The two upload screens both surface a
    // rejection to the user.
    throw new Error('Open a patient chart before uploading device data.');
  }

  return patientId;
}

/**
 * @param {FormData} formData the legacy multipart body, carrying `file-field`
 * @param {string} source which CSV dialect this file is written in
 */
async function uploadDeviceCsv(formData, source) {
  const patientId = currentPatientId();
  // `file-field` is keyon's name for the part; `file` is the API's. Both are
  // read so a caller that has already migrated does not have to be special-cased.
  const file = formData.get('file-field') || formData.get('file');

  if (!file) {
    throw new Error('No file was selected.');
  }

  const body = new FormData();
  body.append('patient_id', String(patientId));
  body.append('source', source);
  body.append('file', file);

  const queued = await createDeviceImport(body);
  return awaitImport(queued);
}

/**
 * Poll row 276 until the queue has finished with the file.
 *
 * Row 276 exists because the legacy routes answered `{status}` and nothing else,
 * so "the upload succeeded" and "the upload succeeded and discarded four hundred
 * readings" were the same response. The rejections come back in `errors` and are
 * returned to the caller rather than swallowed.
 */
async function awaitImport(queued) {
  const importId = queued && queued.import_id;

  if (!importId) {
    return queued;
  }

  let latest = queued;

  for (let attempt = 0; attempt < IMPORT_POLL_ATTEMPTS; attempt += 1) {
    // Asked before waiting, not after: a short file is often already mapped by
    // the time the 202 gets back, and making everyone wait a second for the
    // common case is a second of nothing happening.
    latest = await getDeviceImport(importId);

    if (latest && latest.status !== 'queued') {
      return latest;
    }

    if (attempt < IMPORT_POLL_ATTEMPTS - 1) {
      await sleep(IMPORT_POLL_INTERVAL_MS);
    }
  }

  // Still queued after the ceiling. The upload itself did succeed, so this is
  // "not finished yet" rather than "failed", and it says so.
  const error = new Error(
    'The file was uploaded and is still being processed. Check the Watch/Vitals pages in a few minutes.'
  );
  error.deviceImport = latest;
  throw error;
}

export function uploadBloodPressureDeviceCsv(formData) {
  return uploadDeviceCsv(formData, 'blood_pressure');
}

export function uploadWeightBloodPressureDeviceCsv(formData) {
  return uploadDeviceCsv(formData, 'weight_blood_pressure');
}

export function uploadFreeStyleLibreLinkDeviceCsv(formData) {
  return uploadDeviceCsv(formData, 'freestyle_librelink');
}

export function uploadFreeStyleLibreLinkDeviceCsv2(formData) {
  return uploadDeviceCsv(formData, 'freestyle_librelink_v2');
}

/**
 * Named for keyon's `uploadFore` route — the Fora meter, spelled without the
 * `a` there and in `src/data/devices.js`. The API's dialect key keeps the
 * legacy spelling (`fore`) so the two do not disagree.
 */
export function uploadForeDeviceCsv(formData) {
  return uploadDeviceCsv(formData, 'fore');
}
